import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Mic, MicOff, Loader2, Settings as SettingsIcon, Activity } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { JarvisOrb } from "@/components/jarvis/JarvisOrb";
import { JarvisTranscriptStream } from "@/components/jarvis/JarvisTranscriptStream";
import { PersonaSelector, loadPersonaKey, savePersonaKey, hasStoredPersonaKey, type PersonaKey } from "@/components/jarvis/PersonaSelector";
import { useJarvisRuntime } from "@/hooks/useJarvisRuntime";

type Mode = "passive" | "invocation" | "proactive";

export default function Jarvis() {
  const { user } = useAuth();
  const [orgId, setOrgId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("invocation");
  const [skipTTS, setSkipTTS] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [wakeWords, setWakeWords] = useState<string[]>(["jarvis", "syra"]);
  const [defaultPersonaId, setDefaultPersonaId] = useState<string | null>(null);
  const [personaKey, setPersonaKey] = useState<PersonaKey>(() => loadPersonaKey());

  const handlePersonaChange = useCallback((v: PersonaKey) => {
    setPersonaKey(v);
    savePersonaKey(v);
  }, []);

  // Resolve org do usuário + carrega defaults Jarvis
  useEffect(() => {
    if (!user?.id) return;
    void supabase
      .from("org_members")
      .select("org_id")
      .eq("user_id", user.id)
      .limit(1)
      .single()
      .then(async ({ data }) => {
        if (!data?.org_id) return;
        setOrgId(data.org_id);
        const { data: org } = await supabase
          .from("organizations")
          .select("jarvis_wake_words, jarvis_default_persona_id, jarvis_default_persona_key, jarvis_default_mode")
          .eq("id", data.org_id)
          .single();
        if (org) {
          if (org.jarvis_wake_words?.length) setWakeWords(org.jarvis_wake_words);
          if (org.jarvis_default_persona_id) setDefaultPersonaId(org.jarvis_default_persona_id);
          if (org.jarvis_default_mode) setMode(org.jarvis_default_mode as Mode);
          // Fallback: se org tem persona_key default E usuário ainda não escolheu uma E não há persona_id customizado
          const orgKey = (org as { jarvis_default_persona_key?: string | null }).jarvis_default_persona_key;
          if (
            (orgKey === "syra" || orgKey === "jarvis") &&
            !hasStoredPersonaKey() &&
            !org.jarvis_default_persona_id
          ) {
            setPersonaKey(orgKey);
          }
        }
      });
  }, [user?.id]);

  const runtime = useJarvisRuntime({
    sessionId: sessionId ?? "",
    mode,
    wakeWords,
    skipTTS,
    personaKey,
    onError: (e) => toast.error(`Jarvis: ${e}`),
    onResponse: (r) => console.log("[jarvis] response", r),
  });

  const startSession = useCallback(async () => {
    if (!orgId) {
      toast.error("Organização não encontrada");
      return;
    }
    setBootstrapping(true);
    try {
      // 1. create session (com persona padrão da org se houver)
      const createRes = await supabase.functions.invoke("jarvis-session", {
        body: {
          action: "create",
          org_id: orgId,
          mode,
          language: "pt-BR",
          persona_key: personaKey,
          ...(defaultPersonaId ? { persona_id: defaultPersonaId } : {}),
        },
      });
      if (createRes.error || !createRes.data?.session?.id) {
        throw new Error(createRes.error?.message || "Falha ao criar sessão");
      }
      const newSessionId = createRes.data.session.id as string;

      // 2. warmup
      await supabase.functions.invoke("jarvis-session", {
        body: { action: "warmup", session_id: newSessionId },
      });
      // 3. transition → listening
      await supabase.functions.invoke("jarvis-session", {
        body: { action: "transition", session_id: newSessionId, new_status: "listening" },
      });

      setSessionId(newSessionId);

      // 4. start mic runtime (passa session id direto p/ evitar race com setState)
      await runtime.start(newSessionId);
      toast.success("Jarvis ouvindo");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Erro ao iniciar: ${msg}`);
    } finally {
      setBootstrapping(false);
    }
  }, [orgId, mode, defaultPersonaId, personaKey, runtime]);

  const stopSession = useCallback(async () => {
    runtime.stop();
    if (sessionId) {
      try {
        await supabase.functions.invoke("jarvis-session", {
          body: { action: "transition", session_id: sessionId, new_status: "ending" },
        });
        await supabase.functions.invoke("jarvis-session", {
          body: { action: "transition", session_id: sessionId, new_status: "completed" },
        });
      } catch (e) {
        console.warn("end session error", e);
      }
    }
    setSessionId(null);
    toast.info("Sessão encerrada");
  }, [sessionId, runtime]);

  const isRunning = runtime.state !== "idle" && runtime.state !== "error";

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-headline">Jarvis</h1>
          <p className="text-muted-foreground mt-1">
            Assistente de voz em tempo real com wake-word, VAD e barge-in.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/jarvis/telemetry">
              <Activity className="w-4 h-4 mr-2" /> Telemetria
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/jarvis/settings">
              <SettingsIcon className="w-4 h-4 mr-2" /> Configurar
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6">
        {/* Painel de controle + Orb */}
        <Card className="p-8 flex flex-col items-center justify-between min-h-[520px]">
          <div className="w-full flex items-center justify-between mb-4">
            <Badge variant="outline" className="capitalize">{mode}</Badge>
            {sessionId && <Badge variant="secondary" className="font-mono text-[10px]">{sessionId.slice(0, 8)}…</Badge>}
          </div>

          <PersonaSelector
            value={personaKey}
            onChange={handlePersonaChange}
            disabled={isRunning}
            className="w-full mb-4"
          />

          <JarvisOrb state={runtime.state} level={runtime.level} size={260} />

          {runtime.lastResponse && (
            <div className="w-full mt-6 p-4 rounded-lg bg-muted/40 border border-border/50">
              <div className="text-xs text-muted-foreground mb-1 flex items-center justify-between">
                <span>Última resposta</span>
                <span className="tabular-nums">{runtime.lastResponse.e2eMs}ms · {runtime.lastResponse.provider}</span>
              </div>
              <div className="text-sm text-foreground line-clamp-3">{runtime.lastResponse.text}</div>
            </div>
          )}

          <div className="w-full mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="mode" className="text-xs">Modo</Label>
                <Select value={mode} onValueChange={(v) => setMode(v as Mode)} disabled={isRunning}>
                  <SelectTrigger id="mode" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="passive">Passivo</SelectItem>
                    <SelectItem value="invocation">Invocação (wake-word)</SelectItem>
                    <SelectItem value="proactive">Proativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2 pb-1">
                <Switch id="skipTTS" checked={skipTTS} onCheckedChange={setSkipTTS} disabled={isRunning} />
                <Label htmlFor="skipTTS" className="text-xs">Apenas texto (sem TTS)</Label>
              </div>
            </div>

            {!isRunning ? (
              <Button
                onClick={startSession}
                disabled={!orgId || bootstrapping}
                size="lg"
                className="w-full"
              >
                {bootstrapping ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Iniciando…</>
                ) : (
                  <><Mic className="w-4 h-4 mr-2" /> Iniciar sessão</>
                )}
              </Button>
            ) : (
              <Button onClick={stopSession} variant="destructive" size="lg" className="w-full">
                <MicOff className="w-4 h-4 mr-2" /> Encerrar
              </Button>
            )}
          </div>
        </Card>

        {/* Stream de transcrição */}
        <Card className="p-6 flex flex-col min-h-[520px]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Transcrição ao vivo</h2>
            <Badge variant="outline" className="text-[10px]">
              Realtime
            </Badge>
          </div>
          <div className="flex-1 min-h-0">
            <JarvisTranscriptStream sessionId={sessionId} />
          </div>
        </Card>
      </div>

      <div className="mt-6 text-xs text-muted-foreground">
        Wake-words ativas: <code className="text-foreground">jarvis</code>, <code className="text-foreground">syra</code>.
        No modo invocação, o assistente só responde quando ouvir uma delas.
      </div>
    </div>
  );
}
